import {
  BookOpen,
  Briefcase,
  Building2,
  Calendar,
  Database,
  Eye,
  Globe,
  Heart,
  Info,
  Loader2,
  MapPin,
  Palette,
  Plus,
  Save,
  Target,
  Trash2,
  UserCog,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/components/shared";
import { companyProfileService, dataSourceService } from "@/lib/services";
import type {
  CompanyContextPreview,
  CompanyProfile,
  CompanyProfileUpdate,
  DataCollection,
  LeadershipEntry,
} from "@/types";
import { BrandingSection } from "./companyProfile/BrandingSection";
import { inputClass, labelClass, textareaClass } from "./companyProfile/styles";

// ---------------------------------------------------------------------------
// Form state (mirrors CompanyProfileUpdate but with concrete defaults)
// ---------------------------------------------------------------------------

/** Internal form entry with a stable key for React list rendering. */
interface FormLeadershipEntry extends LeadershipEntry {
  _id: string;
}

let _nextLeaderId = 1;
function makeLeaderId(): string {
  return `leader-${_nextLeaderId++}-${Date.now()}`;
}

interface FormData {
  company_name: string;
  industry: string;
  description: string;
  mission: string;
  values: string;
  founded_year: string; // kept as string for the input, parsed to int on submit
  headquarters: string;
  employee_count: string;
  website: string;
  products_services: string;
  leadership: FormLeadershipEntry[];
  key_terminology: string;
  brand_style: string;
  // Brand kit (structured) — flattened for the form
  brand_primary: string;
  brand_secondary: string;
  brand_accents: string; // comma-separated hex list
  brand_background: string;
  brand_text: string;
  brand_font_heading: string;
  brand_font_body: string;
  brand_tone: string;
  knowledge_base_collection_uuid: string; // UUID string for <select>
}

export function profileToForm(profile: CompanyProfile | null): FormData {
  const kit = profile?.brand_kit ?? null;
  const palette = kit?.palette ?? null;
  const fonts = kit?.fonts ?? null;
  return {
    company_name: profile?.company_name ?? "",
    industry: profile?.industry ?? "",
    description: profile?.description ?? "",
    mission: profile?.mission ?? "",
    values: profile?.values ?? "",
    founded_year: profile?.founded_year?.toString() ?? "",
    headquarters: profile?.headquarters ?? "",
    employee_count: profile?.employee_count ?? "",
    website: profile?.website ?? "",
    products_services: profile?.products_services ?? "",
    leadership: (profile?.leadership ?? []).map((e) => ({ ...e, _id: makeLeaderId() })),
    key_terminology: profile?.key_terminology ?? "",
    brand_style: profile?.brand_style ?? "",
    brand_primary: palette?.primary ?? "",
    brand_secondary: palette?.secondary ?? "",
    brand_accents: (palette?.accents ?? []).join(", "),
    brand_background: palette?.background ?? "",
    brand_text: palette?.text ?? "",
    brand_font_heading: fonts?.heading ?? "",
    brand_font_body: fonts?.body ?? "",
    brand_tone: kit?.tone ?? "",
    knowledge_base_collection_uuid: profile?.knowledge_base_collection_uuid ?? "",
  };
}

/** Build a structured BrandKit from flat form fields, or null when all empty. */
function formToBrandKit(form: FormData): CompanyProfileUpdate["brand_kit"] {
  const accents = form.brand_accents
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const palette = {
    primary: form.brand_primary || null,
    secondary: form.brand_secondary || null,
    accents: accents.length > 0 ? accents : null,
    background: form.brand_background || null,
    text: form.brand_text || null,
  };
  const fonts = {
    heading: form.brand_font_heading || null,
    body: form.brand_font_body || null,
  };
  const hasPalette = Object.values(palette).some((v) => v !== null);
  const hasFonts = Object.values(fonts).some((v) => v !== null);
  const tone = form.brand_tone || null;
  if (!hasPalette && !hasFonts && !tone) {
    return null;
  }
  return {
    palette: hasPalette ? palette : null,
    fonts: hasFonts ? fonts : null,
    tone,
  };
}

export function formToPayload(form: FormData): CompanyProfileUpdate {
  return {
    company_name: form.company_name || null,
    industry: form.industry || null,
    description: form.description || null,
    mission: form.mission || null,
    values: form.values || null,
    founded_year: form.founded_year ? Number.parseInt(form.founded_year, 10) : null,
    headquarters: form.headquarters || null,
    employee_count: form.employee_count || null,
    website: form.website || null,
    products_services: form.products_services || null,
    leadership: form.leadership.length > 0 ? form.leadership.map(({ name, title }) => ({ name, title })) : null,
    key_terminology: form.key_terminology || null,
    brand_style: form.brand_style || null,
    brand_kit: formToBrandKit(form),
    knowledge_base_collection_uuid: form.knowledge_base_collection_uuid || null,
  };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SectionHeading({
  icon: Icon,
  title,
}: Readonly<{
  icon: React.ElementType;
  title: string;
}>) {
  return (
    <div className="flex items-center gap-2 mb-4 pt-2">
      <div className="h-8 w-8 rounded-lg bg-brand-primary/10 flex items-center justify-center">
        <Icon className="h-4 w-4 text-brand-primary" />
      </div>
      <h3 className="text-base font-semibold text-content-primary">{title}</h3>
    </div>
  );
}

function LeadershipEditor({
  entries,
  onChange,
}: Readonly<{
  entries: FormLeadershipEntry[];
  onChange: (entries: FormLeadershipEntry[]) => void;
}>) {
  const add = () => onChange([...entries, { name: "", title: "", _id: makeLeaderId() }]);
  const remove = (i: number) => onChange(entries.filter((_, idx) => idx !== i));
  const update = (i: number, field: keyof LeadershipEntry, value: string) => {
    const next = [...entries];
    next[i] = { ...next[i], [field]: value };
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {entries.map((entry, i) => (
        <div key={entry._id} className="flex gap-2 items-start">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Name"
              value={entry.name}
              onChange={(e) => update(i, "name", e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="flex-1">
            <input
              type="text"
              placeholder="Title / Role"
              value={entry.title}
              onChange={(e) => update(i, "title", e.target.value)}
              className={inputClass}
            />
          </div>
          <button
            type="button"
            onClick={() => remove(i)}
            className="p-2 text-content-tertiary hover:text-red-400 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="flex items-center gap-1.5 text-sm text-brand-primary hover:text-brand-primary/80 transition-colors"
      >
        <Plus className="h-4 w-4" />
        Add leader
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Context Preview Modal
// ---------------------------------------------------------------------------
function ContextPreviewModal({
  preview,
  onClose,
}: Readonly<{
  preview: CompanyContextPreview;
  onClose: () => void;
}>) {
  return (
    <div className="fixed inset-0 bg-surface-overlay flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-brand-primary" />
            <h3 className="text-lg font-semibold text-content-primary">System Prompt Context Preview</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-interactive-active rounded-lg transition-colors">
            <X className="h-5 w-5 text-content-tertiary" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          <div className="flex items-center gap-2 text-sm text-content-secondary">
            <Info className="h-4 w-4" />
            <span>
              This text is automatically prepended to every AI conversation's system prompt (~{preview.token_estimate}{" "}
              tokens).
            </span>
          </div>
          <pre className="bg-surface-secondary border border-border-secondary rounded-lg p-4 text-sm text-content-primary whitespace-pre-wrap font-mono leading-relaxed overflow-auto max-h-[50vh]">
            {preview.context_text}
          </pre>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-border-primary">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg text-sm font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export function AdminCompanyProfile() {
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [form, setForm] = useState<FormData>(profileToForm(null));
  const [dirty, setDirty] = useState(false);

  // Context preview
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<CompanyContextPreview | null>(null);

  // Data sources for knowledge base picker
  const [dataSources, setDataSources] = useState<DataCollection[]>([]);

  // Logo blob URL (fetched with auth headers)
  const [logoBlobUrl, setLogoBlobUrl] = useState<string | null>(null);

  // Load profile and data sources on mount
  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const [profileRes, dsRes] = await Promise.all([
        companyProfileService.get(),
        dataSourceService.list({ pageSize: 100 }),
      ]);
      setProfile(profileRes.data);
      setForm(profileToForm(profileRes.data));
      setDataSources(dsRes.data ?? []);
      setDirty(false);

      // Fetch logo with auth if available
      if (profileRes.data?.logo_url) {
        try {
          const { getHeaders } = await import("@/lib/api");
          const { getApiBaseUrl } = await import("@/config/runtime");
          const headers = await getHeaders();
          const logoRes = await fetch(`${getApiBaseUrl()}${profileRes.data.logo_url}`, { headers });
          if (logoRes.ok) {
            const blob = await logoRes.blob();
            setLogoBlobUrl(URL.createObjectURL(blob));
          }
        } catch {
          // Non-critical — logo preview won't show
        }
      } else {
        setLogoBlobUrl(null);
      }
    } catch {
      showToast("Failed to load company profile", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Refresh only the logo preview after an upload — deliberately does NOT
  // reload the profile, which would discard unsaved form edits.
  const refreshLogoPreview = useCallback(async () => {
    try {
      const profileRes = await companyProfileService.get();
      if (profileRes.data?.logo_url) {
        const { getHeaders } = await import("@/lib/api");
        const { getApiBaseUrl } = await import("@/config/runtime");
        const headers = await getHeaders();
        const logoRes = await fetch(`${getApiBaseUrl()}${profileRes.data.logo_url}`, { headers });
        if (logoRes.ok) {
          const blob = await logoRes.blob();
          setLogoBlobUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(blob);
          });
        }
      }
    } catch {
      // Non-critical — the upload toast already reported success
    }
  }, []);

  // Field change handler
  const setField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  // Save
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.company_name.trim()) {
      showToast("Company name is required", "error");
      return;
    }

    try {
      setSaving(true);
      const payload = formToPayload(form);
      const res = await companyProfileService.upsert(payload);
      setProfile(res.data);
      setForm(profileToForm(res.data));
      setDirty(false);
      showToast("Company profile saved", "success");
    } catch {
      showToast("Failed to save company profile", "error");
    } finally {
      setSaving(false);
    }
  };

  // Preview context
  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      const res = await companyProfileService.previewContext();
      setPreview(res.data);
    } catch {
      showToast("Failed to load context preview", "error");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-content-tertiary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-brand-primary/10 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-brand-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-content-primary">Company Profile</h2>
            <p className="text-sm text-content-tertiary">
              Configure your organization's identity. This information is automatically injected into every AI
              conversation.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePreview}
            disabled={previewLoading || !profile}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-content-secondary bg-surface-secondary hover:bg-interactive-active rounded-lg border border-border-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
            Preview AI Context
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 flex gap-3">
        <Info className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
        <div className="text-sm text-content-secondary">
          <p>
            The company profile is used to auto-generate a concise context block that gets prepended to{" "}
            <strong>every</strong> AI system prompt across the platform. This makes all AI assistants aware of your
            organization without per-agent configuration.
          </p>
          {profile?.updated_by_name && (
            <p className="mt-1 text-content-tertiary">
              Last updated by {profile.updated_by_name} on {new Date(profile.updated_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Core Identity */}
        <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
          <SectionHeading icon={Building2} title="Core Identity" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label htmlFor="company-name" className={labelClass}>
                Company Name <span className="text-red-400">*</span>
              </label>
              <input
                id="company-name"
                type="text"
                value={form.company_name}
                onChange={(e) => setField("company_name", e.target.value)}
                placeholder="e.g. Acme Corporation"
                className={inputClass}
                required
              />
            </div>

            <div>
              <label htmlFor="company-industry" className={labelClass}>
                Industry
              </label>
              <input
                id="company-industry"
                type="text"
                value={form.industry}
                onChange={(e) => setField("industry", e.target.value)}
                placeholder="e.g. Technology, Healthcare, Finance"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>
                <Globe className="inline h-3.5 w-3.5 mr-1" />
                Website
              </label>
              <input
                type="url"
                value={form.website}
                onChange={(e) => setField("website", e.target.value)}
                placeholder="https://www.example.com"
                className={inputClass}
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="company-description" className={labelClass}>
                Description
              </label>
              <textarea
                id="company-description"
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="Brief description of what your company does..."
                rows={3}
                className={textareaClass}
              />
            </div>
          </div>
        </div>

        {/* Mission & Values */}
        <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
          <SectionHeading icon={Target} title="Mission & Values" />

          <div className="space-y-4">
            <div>
              <label className={labelClass}>
                <Target className="inline h-3.5 w-3.5 mr-1" />
                Mission Statement
              </label>
              <textarea
                value={form.mission}
                onChange={(e) => setField("mission", e.target.value)}
                placeholder="Your organization's mission..."
                rows={2}
                className={textareaClass}
              />
            </div>

            <div>
              <label className={labelClass}>
                <Heart className="inline h-3.5 w-3.5 mr-1" />
                Core Values
              </label>
              <textarea
                value={form.values}
                onChange={(e) => setField("values", e.target.value)}
                placeholder="List your organization's core values..."
                rows={2}
                className={textareaClass}
              />
            </div>
          </div>
        </div>

        {/* Company Details */}
        <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
          <SectionHeading icon={Briefcase} title="Company Details" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>
                <Calendar className="inline h-3.5 w-3.5 mr-1" />
                Founded Year
              </label>
              <input
                type="number"
                value={form.founded_year}
                onChange={(e) => setField("founded_year", e.target.value)}
                placeholder="e.g. 2015"
                min={1800}
                max={2100}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>
                <MapPin className="inline h-3.5 w-3.5 mr-1" />
                Headquarters
              </label>
              <input
                type="text"
                value={form.headquarters}
                onChange={(e) => setField("headquarters", e.target.value)}
                placeholder="e.g. New York, NY"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>
                <Users className="inline h-3.5 w-3.5 mr-1" />
                Employee Count
              </label>
              <input
                type="text"
                value={form.employee_count}
                onChange={(e) => setField("employee_count", e.target.value)}
                placeholder="e.g. 500, 1000-5000"
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* Products & Services */}
        <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
          <SectionHeading icon={Briefcase} title="Products & Services" />

          <div>
            <label htmlFor="company-products-services" className={labelClass}>
              What does your organization offer?
            </label>
            <textarea
              id="company-products-services"
              value={form.products_services}
              onChange={(e) => setField("products_services", e.target.value)}
              placeholder="Describe your main products, services, or offerings..."
              rows={3}
              className={textareaClass}
            />
          </div>
        </div>

        {/* Leadership */}
        <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
          <SectionHeading icon={UserCog} title="Leadership Team" />

          <p className="text-sm text-content-tertiary mb-4">
            Add key leaders so the AI can reference them in conversations.
          </p>

          <LeadershipEditor entries={form.leadership} onChange={(entries) => setField("leadership", entries)} />
        </div>

        {/* Terminology */}
        <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
          <SectionHeading icon={BookOpen} title="Key Terminology" />

          <div>
            <label htmlFor="company-key-terminology" className={labelClass}>
              Company-specific jargon, acronyms, or terminology
            </label>
            <textarea
              id="company-key-terminology"
              value={form.key_terminology}
              onChange={(e) => {
                if (e.target.value.length <= 5000) {
                  setField("key_terminology", e.target.value);
                }
              }}
              maxLength={5000}
              placeholder="e.g. OKR = Objectives & Key Results, S1 = Severity 1 incident, CSAT = Customer Satisfaction..."
              rows={3}
              className={textareaClass}
            />
            <div className="mt-1.5 flex items-start justify-between gap-4">
              <p className="text-xs text-content-tertiary">
                Help the AI understand your internal language. Separate entries with commas or new lines.
              </p>
              <span
                className={`text-xs shrink-0 ${form.key_terminology.length >= 5000 ? "text-amber-400 font-medium" : "text-content-quaternary"}`}
              >
                {form.key_terminology.length.toLocaleString()}/5,000
              </span>
            </div>
            {form.key_terminology.length >= 5000 && (
              <p className="mt-1.5 text-xs text-amber-400">
                Character limit reached. Consider moving detailed terminology into a document and adding it to a data
                source linked below, or shortening to terms that are unique to your business (not general industry
                terms).
              </p>
            )}
          </div>
        </div>

        {/* Branding */}
        <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
          <SectionHeading icon={Palette} title="Export Branding" />

          <p className="text-sm text-content-tertiary mb-4">
            Set your structured brand kit (colors, fonts) and upload your logo. These flow into AI-generated output —
            HTML dashboards, documents, and exports — so generated artifacts come out on-brand.
          </p>

          <BrandingSection
            fields={form}
            setField={setField}
            logoBlobUrl={logoBlobUrl}
            onLogoUploaded={refreshLogoPreview}
          />
        </div>

        {/* Knowledge Base */}
        <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
          <SectionHeading icon={Database} title="Knowledge Base" />

          <p className="text-sm text-content-tertiary mb-4">
            Link a data source to serve as the company-wide knowledge base. This allows AI assistants to search company
            documentation (handbooks, policies, etc.) across all conversations.
          </p>

          <div>
            <label className={labelClass}>
              <Database className="inline h-3.5 w-3.5 mr-1" />
              Data Source
            </label>
            <select
              value={form.knowledge_base_collection_uuid}
              onChange={(e) => setField("knowledge_base_collection_uuid", e.target.value)}
              className={inputClass}
            >
              <option value="">None (no knowledge base linked)</option>
              {dataSources.map((ds) => (
                <option key={ds.uuid} value={ds.uuid}>
                  {ds.name}
                  {ds.status ? ` (${ds.status})` : ""}
                  {ds.fileCount ? ` - ${ds.fileCount} files` : ""}
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-content-tertiary">
              Select an existing data source or create one in the Data Sources section first.
            </p>
          </div>
        </div>

        {/* Save button */}
        <div className="flex items-center justify-between pt-2 pb-8">
          <div className="text-sm text-content-tertiary">
            {(() => {
              if (dirty) return <span className="text-amber-400">Unsaved changes</span>;
              if (profile) return "All changes saved";
              return "No profile configured yet";
            })()}
          </div>
          <button
            type="submit"
            disabled={saving || !dirty}
            className="flex items-center gap-2 px-5 py-2.5 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {profile ? "Save Changes" : "Create Profile"}
          </button>
        </div>
      </form>

      {/* Context Preview Modal */}
      {preview && <ContextPreviewModal preview={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
